import axios from "axios";
import { compressImage } from "./imageCompress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// JWT storage helpers. We persist the access token in localStorage and attach
// it as `Authorization: Bearer ...` on every request. This works around
// mobile browsers (Samsung Internet, iOS Safari in PWA mode) that silently
// drop our SameSite=None httpOnly cookie — which previously caused users to
// appear logged in but get 401 on every API call.
const TOKEN_KEY = "voxly:access_token";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function clearStoredToken() {
  setStoredToken(null);
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // keep cookies as a fallback for desktop browsers
});

// Attach the Bearer token (when present) to every outgoing request.
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Endpoints where a 401 is *expected* (user simply isn't logged in yet) and
// should NOT trigger a forced sign-out. Every other 401 means our session
// cookie was rejected — almost always a stale token after a server redeploy
// or JWT-secret rotation. We bounce the user back to the auth screen so they
// can re-authenticate instead of staring at broken buttons.
const AUTH_PUBLIC_PATHS = [
  "/auth/me",
  "/auth/login",
  "/auth/signup",
  "/auth/logout",
  "/auth/session",
  "/auth/magic-link",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/phone",
];

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !AUTH_PUBLIC_PATHS.some((p) => url.includes(p))) {
      // Clear the stored token immediately so subsequent calls don't loop.
      clearStoredToken();
      // Fire a global event so AuthProvider can clear state and redirect.
      // We only fire once per "session" of 401s to avoid toast spam.
      if (typeof window !== "undefined" && !window.__voxlyAuthExpiring) {
        window.__voxlyAuthExpiring = true;
        setTimeout(() => { window.__voxlyAuthExpiring = false; }, 3000);
        window.dispatchEvent(new CustomEvent("voxly:session-expired"));
      }
    }
    return Promise.reject(err);
  }
);

const FALLBACK_AVATAR = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200";

export function fileUrl(storagePath) {
  if (!storagePath) return null;
  return `${API}/files/${storagePath}`;
}

export function avatarUrl(picture) {
  return picture || FALLBACK_AVATAR;
}

/**
 * Upload a file with client-side image compression + progress reporting.
 *
 * @param {File} file
 * @param {string} kind  one of "avatar" | "cover" | "media" | "voice" | "audio" | "recording" | "selfie" | "verification"
 * @param {{ onProgress?: (pct:number) => void, compress?: boolean }} [opts]
 * @returns {Promise<{file_id:string, path:string, size:number}>}
 */
export async function uploadFile(file, kind = "media", opts = {}) {
  const { onProgress, compress = true } = opts;
  let toUpload = file;
  if (compress && file?.type?.startsWith("image/")) {
    try {
      toUpload = await compressImage(file);
    } catch {
      toUpload = file;
    }
  }
  const fd = new FormData();
  fd.append("file", toUpload);
  fd.append("kind", kind);
  const { data } = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (!onProgress) return;
      // evt.total may be undefined for some browsers; fall back gracefully.
      const total = evt.total || toUpload.size || 1;
      const pct = Math.min(100, Math.round((evt.loaded / total) * 100));
      onProgress(pct);
    },
  });
  return data; // { file_id, path, size }
}
